import { useEffect, useState } from 'react'

import i18n from 'i18n'

interface I18nReadyState {
  /** 현재 언어의 translation 네임스페이스가 로드 완료됐는지. */
  ready: boolean
  /** ready/언어가 실제로 바뀔 때만 값이 변하는 키(모달 생성 effect 의존성용). */
  revision: string
}

function computeState(): I18nReadyState {
  const ready = i18n.isInitialized && i18n.hasLoadedNamespace('translation')
  return { ready, revision: `${i18n.language ?? ''}:${ready}` }
}

/**
 * 번역 가용 상태(현재 언어 + 로드 완료 여부)를 구독한다.
 *
 * confirmBase 같은 명령형 모달은 생성 시점에 `i18n.t()` 를 한 번만 평가하고,
 * 점검/공지 가드는 앱 부팅 초기(번역 비동기 로드 전)에 모달을 띄운다.
 * - `ready` 가 false 면 모달 생성을 보류해, 번역이 비어 보이거나
 *   생성 직후 `loaded` 로 재생성되며 깜빡이는 현상을 막는다.
 * - `revision` 은 ready/언어가 실제로 바뀔 때만 변하므로, 의존성에 넣어도
 *   불필요한(동일 번역) 재생성이 발생하지 않는다. 언어 전환 시에만 재생성된다.
 */
export default function useI18nReady(): I18nReadyState {
  const [state, setState] = useState<I18nReadyState>(computeState)

  useEffect(() => {
    const update = (): void =>
      setState((prev) => {
        const next = computeState()
        return prev.revision === next.revision ? prev : next
      })

    i18n.on('initialized', update)
    i18n.on('loaded', update)
    i18n.on('languageChanged', update)
    update()

    return () => {
      i18n.off('initialized', update)
      i18n.off('loaded', update)
      i18n.off('languageChanged', update)
    }
  }, [])

  return state
}
